import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import useScrollAnimation from '../hooks/useScrollAnimation';
import '../styles/scrollAnimations.css';
import './TransitionBreak.css';

const STAR_COUNT = 120;

const generateStars = () => {
  const stars = [];
  for (let i = 0; i < STAR_COUNT; i++) {
    const x = Math.random() * 100;
    const y = Math.random() * 100;
    const isStatic = Math.random() < 0.25;
    const speed = isStatic ? 0 : 0.15 + Math.random() * 0.5;
    const size = isStatic ? 1 + Math.random() : 1 + Math.random() * 2.2;
    stars.push({
      id: i,
      x,
      y,
      initialY: y,
      speed,
      size,
      duration: 2 + Math.random() * 4,
      delay: Math.random() * 5,
    });
  }
  return stars;
};

const TransitionBreak = () => {
  const { language } = useLanguage();
  const t = translations[language].transitionBreak;
  const [ref, visible] = useScrollAnimation({ threshold: 0.3 });
  const stars = useMemo(() => generateStars(), []);
  const sectionRef = useRef(null);
  const starContainerRef = useRef(null);
  const lastScrollY = useRef(0);
  const [warpActive, setWarpActive] = useState(false);

  useEffect(() => {
    if (visible && !warpActive) {
      setWarpActive(true);
    }
  }, [visible, warpActive]);

  useEffect(() => {
    const section = sectionRef.current;
    const starContainer = starContainerRef.current;
    if (!section || !starContainer) return;

    const starEls = starContainer.querySelectorAll('.tbreak-star');

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.top > vh || rect.bottom < 0) return;

      const scrollY = window.scrollY;
      const sectionTop = section.offsetTop;
      const starScroll = Math.max(0, scrollY - sectionTop + vh * 0.5);
      const velocity = Math.abs(scrollY - lastScrollY.current);
      lastScrollY.current = scrollY;
      const stretch = Math.max(1, Math.min(1 + velocity * 0.06, 3.5));

      starEls.forEach((el, i) => {
        const cfg = stars[i];
        if (!cfg || cfg.speed === 0) {
          if (el) el.style.transform = 'translateX(-50%) scaleY(1)';
          return;
        }
        let pos = (cfg.initialY - starScroll * cfg.speed * 0.04) % 100;
        if (pos < 0) pos += 100;
        el.style.top = pos + '%';
        el.style.transform = `translateX(-50%) scaleY(${stretch})`;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [stars]);

  return (
    <section ref={sectionRef} className={`tbreak-section ${warpActive ? 'tbreak-warped' : ''}`}>
      <div ref={starContainerRef} className="tbreak-stars" aria-hidden="true">
        {stars.map((star) => (
          <div
            key={star.id}
            className="tbreak-star"
            style={{
              left: star.x + '%',
              top: star.y + '%',
              width: star.size,
              height: star.size,
              '--tbreak-star-duration': star.duration + 's',
              animationDelay: star.delay + 's',
            }}
          />
        ))}
      </div>
      <div className="tbreak-glow" />
      <div className="tbreak-vignette" />
      <div
        ref={ref}
        className={`tbreak-inner ${visible ? 'tbreak-visible' : ''}`}
      >
        <p className="tbreak-line tbreak-line-1">{t.line1}</p>
        <p className="tbreak-line tbreak-line-2">{t.line2}</p>
        <div className="tbreak-divider" />
      </div>
    </section>
  );
};

export default TransitionBreak;
